import { useState, useEffect, useRef, useCallback } from 'react';
import { X, ExternalLink, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Loader } from './LoadingSpinner';
import EventCard from './EventCard';
import { events as eventsApi } from '../../services/apiService';

const pluralEvents = (n) => `${n} ${n === 1 ? 'event' : 'events'}`;

/**
 * Props:
 *   group      — { entityType, entityId, entityName, entityUrl, eventCount }
 *   usersMap   — { [userId]: { name, avatar } }
 *   canCancel  — boolean
 *   onClose    — () => void
 *
 * Layout: flex-col, h-full — fills whatever height the parent gives it.
 * The events list scrolls independently via its own overflow-y-auto.
 */
const EntityEventDetail = ({ group, usersMap, canCancel, onClose }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(group.eventCount || 0);
  const [error, setError] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const pageRef = useRef(1);
  const isLoadingRef = useRef(false);
  const eventsListRef = useRef(null);

  const fetchEvents = useCallback(async (page) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    if (page === 1) setLoading(true);

    try {
      const res = await eventsApi.getEntityEvents(
        group.entityType,
        group.entityId,
        { page, limit: 20 }
      );
      const newEvents = res.data || [];
      setEvents(prev => page === 1 ? newEvents : [...prev, ...newEvents]);
      setTotal(res.pagination?.total ?? newEvents.length);
      setHasMore(page < (res.pagination?.totalPages ?? 1));
      pageRef.current = page;
      setError(false);
    } catch {
      setError(true);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [group.entityType, group.entityId]);

  useEffect(() => {
    pageRef.current = 1;
    setEvents([]);
    setHasMore(true);
    setError(false);
    fetchEvents(1);
  }, [fetchEvents]);

  // Infinite scroll via scroll event on the events list div
  useEffect(() => {
    const list = eventsListRef.current;
    if (!list) return;

    const onScroll = () => {
      if (!hasMore || isLoadingRef.current) return;
      if (list.scrollHeight - list.scrollTop - list.clientHeight < 300) {
        fetchEvents(pageRef.current + 1);
      }
    };

    list.addEventListener('scroll', onScroll, { passive: true });
    return () => list.removeEventListener('scroll', onScroll);
  }, [hasMore, fetchEvents]);

  const handleCancel = useCallback(async (eventId) => {
    const reason = window.prompt('Reason for cancellation (optional):');
    if (reason === null) return;
    setActionLoading(eventId);
    try {
      await eventsApi.cancelEvent(eventId, reason || null);
      setEvents(prev => prev.map(e => e.id === eventId
        ? { ...e, is_cancelled: true, cancel_reason: reason || null }
        : e
      ));
      toast.success('Event cancelled — excluded from statistics');
    } catch {
      toast.error('Failed to cancel event');
    } finally {
      setActionLoading(null);
    }
  }, []);

  const handleUncancel = useCallback(async (eventId) => {
    setActionLoading(eventId);
    try {
      await eventsApi.uncancelEvent(eventId);
      setEvents(prev => prev.map(e => e.id === eventId
        ? { ...e, is_cancelled: false, cancel_reason: null, cancelled_by: null }
        : e
      ));
      toast.success('Event restored to statistics');
    } catch {
      toast.error('Failed to restore event');
    } finally {
      setActionLoading(null);
    }
  }, []);

  return (
    <div className="flex flex-col h-full border border-light-border-primary dark:border-dark-border-primary rounded-lg bg-light-bg-primary dark:bg-dark-bg-primary overflow-hidden">

      {/* Fixed header */}
      <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-light-border-primary dark:border-dark-border-primary bg-light-bg-primary dark:bg-dark-bg-primary">
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary text-light-text-tertiary dark:text-dark-text-tertiary transition-colors"
          title="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex-1 min-w-0">
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary leading-none mb-0.5">
            {group.entityType}
          </p>
          <p className="font-medium text-sm text-light-text-primary dark:text-dark-text-primary truncate">
            {group.entityName}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
            {pluralEvents(total)}
          </span>
          {group.entityUrl && (
            <Link
              to={group.entityUrl}
              className="p-1 rounded hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary text-light-text-tertiary dark:text-dark-text-tertiary transition-colors"
              title="Open page"
            >
              <ExternalLink className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>

      {/* Scrollable events list — fills remaining height */}
      <div ref={eventsListRef} className="flex-1 overflow-y-auto">
        {loading && events.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader variant="component" size="md" fullHeight={false} />
          </div>
        ) : error && events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-sm text-red-500">
            <AlertCircle className="h-8 w-8" />
            <p>Failed to load events</p>
            <button
              onClick={() => { setError(false); fetchEvents(pageRef.current); }}
              className="px-3 py-1.5 bg-primary-600 text-white rounded text-xs hover:bg-primary-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
            {events.map(event => (
              <div key={event.id} className="p-4">
                <EventCard
                  event={event}
                  usersMap={usersMap}
                  entity={null}
                  canCancel={canCancel}
                  onCancel={handleCancel}
                  onUncancel={handleUncancel}
                  actionLoading={actionLoading}
                />
              </div>
            ))}

            {loading && (
              <div className="flex justify-center py-4">
                <Loader variant="component" size="sm" fullHeight={false} />
              </div>
            )}

            {!hasMore && events.length > 0 && (
              <p className="text-center text-xs text-light-text-tertiary dark:text-dark-text-tertiary py-4">
                All {total} events loaded
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EntityEventDetail;
